/**
 * Where the bottom-left cookie cog may sit without covering page content.
 *
 * Public Look recheck on #859 (2026-10-22): the cog still covered body text
 * ('Perched' on Sumpter at 1440 while scrolling, 'privacy' and 'Show more' on
 * Wintoon at 375). Two causes: the old check counted text only when it was on
 * top at its center (a clamp fade or any overlay hid the paragraph from the
 * check), and the lift went through React state, so it landed a frame or more
 * after the scroll. This check samples a point grid over each candidate spot
 * with elementsFromPoint and walks the whole stack, so text under a
 * translucent overlay still counts, and the banner writes the result straight
 * to the element inside the same animation frame.
 */
export const COG_LIFT_STEPS = [0, 52, 104, 156, 208, 260]
const PAD = 6
const COLS = 4
const ROWS = 7

type Box = { left: number; top: number; right: number; bottom: number }

const CONTROL =
  'a[href],button,input,select,textarea,summary,[role="button"],[role="link"],[role="slider"],[role="tab"],[contenteditable="true"]'

function ownUi(el: Element, own: Element): boolean {
  return own.contains(el) || el.closest('.v3-sarah,[data-install-bar]') !== null
}

function textAt(doc: Document, el: Element, x: number, y: number, range: Range): boolean {
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType !== 3 || !/\S/.test(child.nodeValue || '')) continue
    range.selectNodeContents(child)
    for (const r of Array.from(range.getClientRects())) {
      if (x >= r.left && x <= r.right && y >= r.top - 2 && y <= r.bottom + 2) return true
    }
  }
  return false
}

/**
 * True when any sampled point in the box lands on text, a control or
 * card-sized media. `own` is the floating widget being placed (the cookie cog
 * or Sarah's launcher); its own subtree never counts as content. Shared by
 * both so they follow one definition of "covers nothing".
 */
export function boxCoversContent(doc: Document, own: Element, box: Box): boolean {
  if (typeof doc.elementsFromPoint !== 'function') return false
  const view = doc.defaultView
  const vw = view?.innerWidth ?? 1024
  const range = doc.createRange()
  const root = doc.documentElement
  for (let i = 0; i < ROWS; i++) {
    const y = box.top + ((box.bottom - box.top) * (i + 0.5)) / ROWS
    for (let j = 0; j < COLS; j++) {
      const x = box.left + ((box.right - box.left) * (j + 0.5)) / COLS
      if (x < 0 || y < 0 || x > vw) continue
      for (const el of doc.elementsFromPoint(x, y)) {
        if (el === root || el === doc.body) break
        if (ownUi(el, own)) continue
        if (el.matches(CONTROL) || el.closest(CONTROL)) return true
        if (el.matches('img,video,canvas,iframe') && el.getBoundingClientRect().width < vw * 0.7) return true
        if (textAt(doc, el, x, y, range)) return true
      }
    }
  }
  return false
}

/** Lift in px for the first clear step, or null when every step covers content. */
export function cogLift(doc: Document, cog: HTMLElement, currentLift: number): number | null {
  const r = cog.getBoundingClientRect()
  const base: Box = { left: r.left, top: r.top + currentLift, right: r.right, bottom: r.bottom + currentLift }
  for (const lift of COG_LIFT_STEPS) {
    const box: Box = {
      left: base.left - PAD,
      top: base.top - lift - PAD,
      right: base.right + PAD,
      bottom: base.bottom - lift + PAD,
    }
    if (box.top < 72) break
    if (!boxCoversContent(doc, cog, box)) return lift
  }
  return null
}
